import { useState } from "react";
import { CalendarHeart, Send, MessageCircle, CheckCircle2 } from "lucide-react";

const WHATSAPP_NUMBER = "917806863141";
const OCCASIONS = ["Bridal / Muhurtham", "Reception", "Engagement", "Party", "Bridesmaid", "Other"];

const inputClass =
    "w-full rounded-sm border border-[#D4AF37]/30 bg-white px-4 py-3 text-sm text-[#2C2A29] outline-none placeholder:text-[#5A5652]/60 focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/20";

export const LeadForm = () => {
    const [form, setForm] = useState({ name: "", phone: "", wedding_date: "", occasion: OCCASIONS[0], message: "" });
    const [status, setStatus] = useState("idle");

    const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

    const submit = (e) => {
        e.preventDefault();
        const lines = [
            "Hi Monisha! New enquiry from the website:",
            `Name: ${form.name}`,
            `Phone: ${form.phone}`,
            form.wedding_date && `Event date: ${form.wedding_date}`,
            `Occasion: ${form.occasion}`,
            form.message && `Note: ${form.message}`,
        ].filter(Boolean);
        window.open(
            `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(lines.join("\n"))}`,
            "_blank",
        );
        setStatus("success");
    };

    return (
        <section data-testid="lead-form-section" className="bg-[#FDFBF7] py-24">
            <div className="mx-auto max-w-2xl px-5">
                <p className="text-center text-xs font-semibold uppercase tracking-[0.25em] text-[#D4AF37]">
                    Prefer not to call?
                </p>
                <h2 className="mt-3 text-center font-serif text-4xl tracking-tight text-[#2C2A29] sm:text-5xl">
                    Drop your details, we'll call you back
                </h2>
                {status === "success" ? (
                    <div
                        data-testid="lead-form-success"
                        className="mt-12 rounded-sm border border-[#D4AF37]/30 bg-[#FAF5EF] p-10 text-center"
                    >
                        <CheckCircle2 className="mx-auto h-12 w-12 text-[#25D366]" />
                        <h3 className="mt-4 font-serif text-3xl text-[#2C2A29]">Almost done, {form.name}!</h3>
                        <p className="mt-3 text-sm leading-relaxed text-[#5A5652]">
                            WhatsApp should have opened with your enquiry filled in — just press Send.
                            If it didn't open, tap the button below.
                        </p>
                        <a
                            href={`https://wa.me/${WHATSAPP_NUMBER}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="lead-success-whatsapp-button"
                            className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#25D366] px-8 py-3.5 text-sm font-extrabold text-white shadow-lg transition-transform duration-200 hover:-translate-y-1"
                        >
                            <MessageCircle className="h-5 w-5" />
                            WhatsApp Now
                        </a>
                    </div>
                ) : (
                    <form
                        onSubmit={submit}
                        data-testid="lead-enquiry-form"
                        className="mt-12 space-y-5 rounded-sm border border-[#D4AF37]/20 bg-white p-8 sm:p-10"
                    >
                        <div className="grid gap-5 sm:grid-cols-2">
                            <input
                                required
                                value={form.name}
                                onChange={set("name")}
                                placeholder="Your name *"
                                data-testid="lead-name-input"
                                className={inputClass}
                            />
                            <input
                                required
                                type="tel"
                                value={form.phone}
                                onChange={set("phone")}
                                placeholder="Phone / WhatsApp number *"
                                data-testid="lead-phone-input"
                                className={inputClass}
                            />
                        </div>
                        <div className="grid gap-5 sm:grid-cols-2">
                            <input
                                type="date"
                                value={form.wedding_date}
                                onChange={set("wedding_date")}
                                data-testid="lead-date-input"
                                className={inputClass}
                            />
                            <select
                                value={form.occasion}
                                onChange={set("occasion")}
                                data-testid="lead-occasion-select"
                                className={inputClass}
                            >
                                {OCCASIONS.map((o) => (
                                    <option key={o} value={o}>{o}</option>
                                ))}
                            </select>
                        </div>
                        <textarea
                            value={form.message}
                            onChange={set("message")}
                            placeholder="Anything we should know? (venue, time, number of people...)"
                            rows={3}
                            data-testid="lead-message-input"
                            className={inputClass}
                        />
                        <button
                            type="submit"
                            data-testid="lead-submit-button"
                            className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#D4AF37] px-8 py-4 text-base font-extrabold text-[#2C2A29] shadow-lg transition-transform duration-200 hover:-translate-y-1"
                        >
                            <CalendarHeart className="h-5 w-5" />
                            Send Enquiry on WhatsApp
                            <Send className="h-4 w-4" />
                        </button>
                        <p className="text-center text-xs text-[#5A5652]">
                            Opens WhatsApp with your details pre-filled — nothing is stored online.
                        </p>
                    </form>
                )}
            </div>
        </section>
    );
};
